import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useI18n } from '../content/i18n';
import './Hero.css';

const Hero = () => {
  const { t, isKo } = useI18n();
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const iconRef = useRef(null);
  const phoneRef = useRef(null);
  const scrollIndicatorRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 텍스트 애니메이션
      gsap.fromTo(
        titleRef.current.children,
        { x: -50, opacity: 0 },
        { 
          x: 0, 
          opacity: 1, 
          duration: 1, 
          ease: 'power3.out',
          stagger: 0.2,
          delay: 0.3
        }
      );

      // 우측 컨텐츠 애니메이션
      gsap.fromTo(
        [iconRef.current, phoneRef.current],
        { x: 50, opacity: 0 },
        { 
          x: 0, 
          opacity: 1, 
          duration: 1.2, 
          ease: 'power3.out',
          stagger: 0.2,
          delay: 0.5
        }
      );

      // 스크롤 인디케이터 애니메이션
      gsap.fromTo(
        scrollIndicatorRef.current,
        { opacity: 0, y: -20 },
        { 
          opacity: 0.7, 
          y: 0, 
          duration: 1, 
          ease: 'power3.out',
          delay: 1.5
        }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    // 스크롤 시 인디케이터 숨기기
    const handleScroll = () => {
      if (scrollIndicatorRef.current) {
        const scrollY = window.scrollY;
        if (scrollY > 100) {
          gsap.to(scrollIndicatorRef.current, {
            opacity: 0,
            duration: 0.3
          });
        } else {
          gsap.to(scrollIndicatorRef.current, {
            opacity: 0.7,
            duration: 0.3
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-content container container--wide">
        <div className="hero-left">
          <div className="hero-title" ref={titleRef}>
            <h1 className="title-line title-top">{t.hero.titleTop}</h1>
            <h2 className="title-line title-middle">{t.hero.titleMiddle}</h2>
            <h3 className="title-line title-bottom">
              <span className="mimic-brand">{t.common.brand}</span>
              {isKo ? <span className="mimic-korean">{t.common.brandKo}</span> : null}
            </h3>
          </div>
          <div className="download-buttons">
            <a 
              href="https://apps.apple.com/us/app/mimic-%EB%AF%B8%EB%AF%B9/id6756565040" 
              target="_blank" 
              rel="noopener noreferrer"
              className="button button--primary download-button ios-button"
              aria-label={t.hero.iosAriaLabel}
            >
              <svg className="button-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <div className="button-text">
                <span className="button-label">{t.hero.iosLabel}</span>
                <span className="button-store">{t.hero.iosStore}</span>
              </div>
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.benefitbros.mimic" 
              target="_blank" 
              rel="noopener noreferrer"
              className="button button--primary download-button android-button"
              aria-label={t.hero.androidAriaLabel}
            >
              <svg className="button-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="button-text">
                <span className="button-label">{t.hero.androidLabel}</span>
                <span className="button-store">{t.hero.androidStore}</span>
              </div>
            </a>
          </div>
        </div>
        <div className="hero-right">
          <div className="app-icon" ref={iconRef}>
            <img src="/resource/new_AppIcon.png" alt={t.hero.appIconAlt} />
          </div>
          <div className="phone-mockup" ref={phoneRef}>
            <div className="phone-frame">
              <video 
                className="phone-video" 
                autoPlay 
                loop 
                muted
                playsInline
                webkit-playsinline="true"
                preload="auto"
                src="/movie/title_mimicking.mp4"
              >
                {t.common.videoUnsupported}
              </video>
            </div>
          </div>
        </div>
      </div>
      <div className="scroll-indicator" ref={scrollIndicatorRef}>
        <div className="mouse">
          <div className="wheel"></div>
        </div>
        <div className="arrow">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </section>
  );
};

export default Hero;

